/**
 * School Years Management Component
 * Coláiste Lurgan Management System - School Year CRUD operations
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useSchoolYears, useDeleteSchoolYear } from '../../hooks/useSchoolYears';
import SchoolYearModal from './SchoolYearModal';
import {
  GraduationCap,
  Plus,
  Pencil,
  Trash2,
  Users,
  Hash
} from 'lucide-react';

export function SchoolYearsManagement() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schoolYearToDelete, setSchoolYearToDelete] = useState(null);
  const [schoolYearModalOpen, setSchoolYearModalOpen] = useState(false);
  const [schoolYearToEdit, setSchoolYearToEdit] = useState(null);

  // Mutations
  const deleteSchoolYearMutation = useDeleteSchoolYear();

  // Get school years data with better error handling
  const {
    data: schoolYearsResponse,
    isLoading,
    isError,
    error
  } = useSchoolYears({}, {
    // Graceful error handling - don't treat empty DB as error
    retry: (failureCount, error) => {
      // Don't retry on 404 (no school years) or 500 from empty DB
      if (error?.response?.status === 404) return false;
      if (error?.response?.status === 500) return false;
      return failureCount < 3;
    },
    // Default to empty state if API fails
    onError: (error) => {
      // School Years API error handled by error state
    }
  });

  // Handle both paginated and non-paginated response formats
  const schoolYears = Array.isArray(schoolYearsResponse)
    ? schoolYearsResponse
    : (schoolYearsResponse?.results || []);

  // Check if school year can be deleted (no students assigned)
  const canDeleteSchoolYear = (schoolYear) => {
    // School year can be deleted if no students are assigned to it
    const canDelete = (schoolYear.student_count || 0) === 0;
    return canDelete;
  };

  // Handle delete school year
  const handleDeleteSchoolYear = (schoolYear) => {
    setSchoolYearToDelete(schoolYear);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSchoolYear = async () => {
    if (!schoolYearToDelete) return;

    try {
      await deleteSchoolYearMutation.mutateAsync(schoolYearToDelete.uuid);
      toast({
        title: t('schoolYears.deleted'),
        description: t('schoolYears.deleteSuccess', { year: schoolYearToDelete.year }),
      });
      setDeleteDialogOpen(false);
      setSchoolYearToDelete(null);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('schoolYears.deleteError'),
        variant: 'destructive',
      });
    }
  };

  // Handle add school year
  const handleAddSchoolYear = () => {
    setSchoolYearToEdit(null);
    setSchoolYearModalOpen(true);
  };

  // Handle edit school year
  const handleEditSchoolYear = (schoolYear) => {
    setSchoolYearToEdit(schoolYear);
    setSchoolYearModalOpen(true);
  };

  // Handle school year modal success
  const handleSchoolYearModalSuccess = (schoolYearData, isEditing) => {
    if (isEditing) {
      toast({
        title: t('schoolYears.updated'),
        description: t('schoolYears.updateSuccess', { year: schoolYearData.year }),
      });
    } else {
      toast({
        title: t('schoolYears.created'),
        description: t('schoolYears.createSuccess', { year: schoolYearData.year }),
      });
    }
  };

  // Debug data available: schoolYearsResponse, isLoading, isError, schoolYears, error

  // Only show error if it's a real error (not just empty database)
  if (isError && error?.response?.status !== 500) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-destructive">
            {t('error.loadingSchoolYears', 'Error loading school years. Please try again.')}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Error: {error?.response?.status} - {error?.message}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                {t('schoolYears.management.title', 'School Years')}
              </CardTitle>
              <CardDescription>
                {t('schoolYears.management.description', 'Manage academic years and session periods')}
              </CardDescription>
            </div>
            <Button className="gap-2" onClick={handleAddSchoolYear}>
              <Plus className="h-4 w-4" />
              {t('schoolYears.actions.addSchoolYear', 'Add School Year')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {t('loading.schoolYears', 'Loading school years...')}
              </p>
            </div>
          ) : schoolYears.length === 0 ? (
            <div className="text-center py-8">
              <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t('schoolYears.empty', 'No school years configured yet. Add your first school year to get started.')}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('schoolYears.fields.year', 'School Year')}</TableHead>
                  <TableHead>{t('schoolYears.fields.displayOrder', 'Display Order')}</TableHead>
                  <TableHead>{t('schoolYears.fields.students', 'Students')}</TableHead>
                  <TableHead>{t('schoolYears.fields.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schoolYears.map((schoolYear) => (
                  <TableRow key={schoolYear.uuid}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-blue-600" />
                        <div>
                          <div className="font-medium">{schoolYear.year}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Hash className="h-3 w-3 text-gray-500" />
                        <span className="font-medium">{schoolYear.display_order}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-green-600" />
                          <span className="font-medium">{schoolYear.student_count || 0}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {schoolYear.student_count === 1 ? 'student' : 'students'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={schoolYear.is_active ? 'default' : 'secondary'}>
                        {schoolYear.is_active
                          ? t('schoolYears.status.active', 'Active')
                          : t('schoolYears.status.inactive', 'Inactive')
                        }
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => handleEditSchoolYear(schoolYear)}
                        >
                          <Pencil className="h-3 w-3" />
                          {t('common.edit', 'Edit')}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-red-600 hover:text-red-800"
                          disabled={!canDeleteSchoolYear(schoolYear)}
                          onClick={() => handleDeleteSchoolYear(schoolYear)}
                        >
                          <Trash2 className="h-3 w-3" />
                          {t('common.delete', 'Delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('schoolYears.delete.title', 'Delete School Year')}
            </DialogTitle>
            <DialogDescription>
              {t('schoolYears.delete.message', 'Are you sure you want to delete "{{schoolYearName}}"? This action cannot be undone.', { schoolYearName: schoolYearToDelete?.year })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteSchoolYear}
              disabled={deleteSchoolYearMutation.isPending}
            >
              {deleteSchoolYearMutation.isPending ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* School Year Creation/Edit Modal */}
      <SchoolYearModal
        isOpen={schoolYearModalOpen}
        onClose={() => setSchoolYearModalOpen(false)}
        schoolYear={schoolYearToEdit}
        onSuccess={handleSchoolYearModalSuccess}
      />
    </>
  );
}

export default SchoolYearsManagement;